import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Form, Input, Select, Button, Spin, Modal, List, Typography, Tag, Descriptions, Empty, Alert } from 'antd';
import { toast } from 'react-toastify';
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { IoMdInformationCircle } from "react-icons/io";

import '../../../css/UserAdmin/Global.css';
import '../../../css/UserAdmin/Schedule.css';
import 'react-toastify/dist/ReactToastify.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';
import ScheduleVisualizer from './ScheduleVisualizer';
import SubjectSelector from './SubjectSelector';
import ScheduleEntryModal from './ScheduleEntryModal';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Option } = Select;
const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL || '';

const getFacultyDisplayInfo = (facultyRef, facultyList) => {
    if (!facultyRef || !facultyList || !facultyList.length) return { fullName: 'N/A (Unassigned)' };
    const faculty = facultyList.find(f => f._id === facultyRef);
    if (!faculty) return { fullName: 'Faculty not found' };
    
    const firstName = faculty.firstName || '';
    const middleName = faculty.middleName && faculty.middleName.trim() !== '' ? ` ${faculty.middleName.trim()}` : '';
    const lastName = faculty.lastName || '';
    let constructedFullName = `${firstName}${middleName} ${lastName}`.trim();
    return { fullName: constructedFullName || 'Name Not Available' };
};


const ScheduleForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();
    const { id: scheduleId } = useParams();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initialDataLoading, setInitialDataLoading] = useState(true);
    const [isInitialSetupComplete, setIsInitialSetupComplete] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [formError, setFormError] = useState(null);

    const isViewMode = location.pathname.includes('/view/');
    const isEditMode = !!scheduleId && !isViewMode;
    const isCreateMode = !scheduleId;

    const [isEntryModalVisible, setIsEntryModalVisible] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);

    const [allFacultyAccounts, setAllFacultyAccounts] = useState([]);
    const [allFacultyProfiles, setAllFacultyProfiles] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [academicTerms, setAcademicTerms] = useState([]);
    const [gradeLevels, setGradeLevels] = useState([]);
    const [strands, setStrands] = useState([]);
    const [sections, setSections] = useState([]);
    const [subjectsList, setSubjectsList] = useState([]);
    const [availableRooms, setAvailableRooms] = useState([]);
    
    const [loadingFaculty, setLoadingFaculty] = useState(false);
    const [loadingAcademicYears, setLoadingAcademicYears] = useState(false);
    const [loadingAcademicTerms, setLoadingAcademicTerms] = useState(false);
    const [loadingGradeLevels, setLoadingGradeLevels] = useState(false);
    const [loadingStrands, setLoadingStrands] = useState(false);
    const [loadingSections, setLoadingSections] = useState(false);
    const [loadingSubjects, setLoadingSubjects] = useState(false);
    const [loadingRooms, setLoadingRooms] = useState(false);

    const watchedScheduleEntries = Form.useWatch('scheduleEntries', form);
    const selectedGradeLevel = Form.useWatch('gradeLevel', form);
    const selectedStrand = Form.useWatch('strand', form);
    const selectedAcademicTerm = Form.useWatch('academicTerm', form);
    const selectedAcademicYear = Form.useWatch('academicYear', form);
    
    const [allSchedulesInContext, setAllSchedulesInContext] = useState([]);

    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const pageTitle = isViewMode ? 'View Class Schedule' : isEditMode ? 'Edit Class Schedule' : 'Create Class Schedule';
    const fieldsDisabled = isViewMode || isSubmitting || initialDataLoading;

    const fetchDataForDropdown = useCallback(async (endpoint, setData, setLoadingState, friendlyName, params = {}) => {
        setLoadingState(true);
        let url = `${API_URL}${endpoint}`;
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') queryParams.append(key, value);
        });
        const queryString = queryParams.toString();
        if (queryString) url += `?${queryString}`;

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error((await response.json().catch(() => ({}))).message || `Failed to fetch ${friendlyName}.`);
            const result = await response.json();
            setData(result.data || (Array.isArray(result) ? result : []));
        } catch (error) {
            console.error(`Error fetching ${friendlyName} from ${url}:`, error);
            if (!isViewMode) toast.error(`Could not load ${friendlyName}: ${error.message.substring(0, 100)}`);
            setData([]);
        } finally {
            setLoadingState(false);
        }
    }, [isViewMode]);

    useEffect(() => {
        if (!isInitialSetupComplete) return;
        if (!selectedGradeLevel) {
            setSections([]);
            form.setFieldsValue({ section: undefined });
            return;
        }
        const params = { gradeLevel: selectedGradeLevel, status: 'Active' };
        if (selectedStrand) params.strand = selectedStrand;
        fetchDataForDropdown('/api/admin/sections', setSections, setLoadingSections, 'sections', params)
            .then(() => form.setFieldsValue({ section: undefined }));
    }, [selectedGradeLevel, selectedStrand, isInitialSetupComplete, form, fetchDataForDropdown]);

    useEffect(() => {
        if (!isInitialSetupComplete) return;
        const currentEntries = form.getFieldValue('scheduleEntries') || [];
        if (!selectedAcademicTerm || !selectedGradeLevel) {
            setSubjectsList([]);
            form.setFieldsValue({ scheduleEntries: currentEntries.filter(e => e.subjectFormField) });
            return;
        }
        const params = { term: selectedAcademicTerm, gradeLevel: selectedGradeLevel, status: 'Active', isArchived: 'false' };
        if (selectedStrand) params.strand = selectedStrand;
        fetchDataForDropdown('/api/admin/subjects', setSubjectsList, setLoadingSubjects, 'subjects', params)
            .then(() => form.setFieldsValue({ scheduleEntries: currentEntries.filter(e => e.subjectFormField) }));
    }, [selectedAcademicTerm, selectedGradeLevel, selectedStrand, isInitialSetupComplete, form, fetchDataForDropdown]);

    useEffect(() => {
        const fetchBaseDropdowns = () => Promise.all([
            fetchDataForDropdown('/api/admin/accounts', setAllFacultyAccounts, setLoadingFaculty, 'faculty accounts', { role: 'Faculty', status: 'Active', isArchived: 'false', limit: 1000 }),
            fetchDataForDropdown('/api/admin/faculty-profiles', setAllFacultyProfiles, setLoadingFaculty, 'faculty profiles', { isArchived: 'false' }),
            fetchDataForDropdown('/api/admin/year-level', setGradeLevels, setLoadingGradeLevels, 'grade levels', { status: 'Active' }),
            fetchDataForDropdown('/api/admin/strands', setStrands, setLoadingStrands, 'strands', { status: 'Active' }),
            fetchDataForDropdown('/api/admin/rooms', setAvailableRooms, setLoadingRooms, 'available rooms')
        ]);

        const setupCreateMode = async () => {
            setInitialDataLoading(true);
            setFormError(null);
            form.resetFields();
            form.setFieldsValue({ status: 'Pending', scheduleEntries: [] });

            try {
                await fetchBaseDropdowns();
                const yearResponse = await fetch(`${API_URL}/api/admin/years?status=Active&limit=1`);
                if (!yearResponse.ok) throw new Error('Failed to fetch active academic year.');
                const yearResult = await yearResponse.json();
                const activeYear = (yearResult.data || [])[0];
                if (!activeYear) throw new Error('No active Academic Year found. Please set one in system settings.');
                
                const termResponse = await fetch(`${API_URL}/api/admin/terms?academicYear=${encodeURIComponent(activeYear.Year)}&status=Active&limit=1`);
                if (!termResponse.ok) throw new Error(`Could not fetch active term for ${activeYear.Year}.`);
                const termResult = await termResponse.json();
                const activeTerm = (termResult.data || [])[0];
                if (!activeTerm) throw new Error(`No active Academic Term found for ${activeYear.Year}.`);
                
                setAcademicYears([activeYear]);
                setAcademicTerms([activeTerm]);
                form.setFieldsValue({ academicYear: activeYear.Year, academicTerm: activeTerm.term });
            } catch (error) {
                toast.error(error.message);
                setFormError(error.message);
            } finally {
                setInitialDataLoading(false);
                setIsInitialSetupComplete(true);
            }
        };

        const setupEditOrViewMode = async () => {
            if (!scheduleId) return;
            setInitialDataLoading(true);
            setFormError(null);

            try {
                await fetchBaseDropdowns();
                const response = await fetch(`${API_URL}/api/admin/class-schedules/${scheduleId}`);
                if (!response.ok) throw new Error((await response.json().catch(() => ({}))).message || 'Failed to fetch schedule details.');
                const scheduleData = (await response.json()).data;
                if (!scheduleData) {
                    toast.error('Schedule data not found.');
                    return navigate('/user-admin/schedule');
                }
                
                const sectionParams = { gradeLevel: scheduleData.gradeLevel, status: 'Active' };
                if (scheduleData.strand) sectionParams.strand = scheduleData.strand;
                const subjectParams = { term: scheduleData.academicTerm, gradeLevel: scheduleData.gradeLevel, status: 'Active', isArchived: 'false' };
                if (scheduleData.strand) subjectParams.strand = scheduleData.strand;

                await Promise.all([
                    fetchDataForDropdown('/api/admin/years', setAcademicYears, setLoadingAcademicYears, 'academic years', { isArchived: 'any' }),
                    fetchDataForDropdown('/api/admin/terms', setAcademicTerms, setLoadingAcademicTerms, 'academic terms', { academicYear: scheduleData.academicYear, isArchived: 'any' }),
                    fetchDataForDropdown('/api/admin/sections', setSections, setLoadingSections, 'sections', sectionParams),
                    fetchDataForDropdown('/api/admin/subjects', setSubjectsList, setLoadingSubjects, 'subjects', subjectParams),
                ]);

                form.setFieldsValue({
                    academicYear: scheduleData.academicYear,
                    academicTerm: scheduleData.academicTerm,
                    gradeLevel: scheduleData.gradeLevel,
                    strand: scheduleData.strand || undefined,
                    section: scheduleData.section,
                    status: scheduleData.status || 'Pending',
                    scheduleEntries: scheduleData.scheduleEntries?.map(entry => ({
                        facultyRef: entry.facultyRef?._id || entry.facultyRef || undefined,
                        room: entry.room,
                        days: entry.days,
                        timeStart: entry.timeStart,
                        timeEnd: entry.timeEnd,
                        subjectFormField: entry.subjectCode,
                    })) || [],
                });
            } catch (error) {
                toast.error(error.message || 'Could not load schedule details.');
                setFormError(error.message || 'Could not load schedule details.');
            } finally {
                setInitialDataLoading(false);
                setIsInitialSetupComplete(true);
            }
        };

        if (isCreateMode) setupCreateMode();
        else setupEditOrViewMode();
    }, [scheduleId, isCreateMode, isEditMode, isViewMode, form, navigate, fetchDataForDropdown]);

    useEffect(() => {
        if (selectedAcademicYear && selectedAcademicTerm) {
            const fetchAllSchedules = async () => {
                try {
                    const response = await fetch(`${API_URL}/api/admin/class-schedules?academicYear=${selectedAcademicYear}&academicTerm=${selectedAcademicTerm}&limit=1000&isArchived=false`);
                    const result = await response.json();
                    const otherSchedules = (result.data || []).filter(s => s._id !== scheduleId);
                    setAllSchedulesInContext(otherSchedules);
                } catch (error) {
                    console.error("Could not fetch schedules for conflict check:", error);
                    setAllSchedulesInContext([]);
                }
            };
            fetchAllSchedules();
        }
    }, [selectedAcademicYear, selectedAcademicTerm, scheduleId]);

    const handleAddEntry = (subject) => {
        if (isViewMode) return;
        setEditingEntry({ subjectFormField: subject.subjectCode });
        setIsEntryModalVisible(true);
    };

    const handleEditEntry = (entryIndex) => {
        if (isViewMode) return;
        const entry = (form.getFieldValue('scheduleEntries') || [])[entryIndex];
        if (entry) setEditingEntry({ ...entry, index: entryIndex });
        setIsEntryModalVisible(true);
    };

    const handleSaveEntry = (values) => {
        const currentEntries = form.getFieldValue('scheduleEntries') || [];
        if (editingEntry.index !== undefined) {
            const updatedEntries = [...currentEntries];
            updatedEntries[editingEntry.index] = { ...updatedEntries[editingEntry.index], ...values };
            form.setFieldsValue({ scheduleEntries: updatedEntries });
        } else {
            form.setFieldsValue({ scheduleEntries: [...currentEntries, values] });
        }
        toast.success(`Subject "${values.subjectFormField}" has been scheduled.`);
        setIsEntryModalVisible(false);
        setEditingEntry(null);
    };
    
    const handleRemoveEntry = (entryIndex) => {
        const currentEntries = form.getFieldValue('scheduleEntries') || [];
        const entryToRemove = currentEntries[entryIndex];
        const newEntries = currentEntries.filter((_, index) => index !== entryIndex);
        form.setFieldsValue({ scheduleEntries: newEntries });
        toast.warn(`Subject "${entryToRemove.subjectFormField}" has been removed from the schedule.`);
        setIsEntryModalVisible(false);
        setEditingEntry(null);
    };

    const handlePreview = () => {
        setFormError(null);
        form.validateFields()
            .then(values => {
                const formattedValues = {
                    ...values,
                    scheduleEntries: values.scheduleEntries?.map(entry => {
                        const subjectObj = subjectsList.find(s => s.subjectCode === entry.subjectFormField);
                        const { fullName } = getFacultyDisplayInfo(entry.facultyRef, allFacultyAccounts);
                        return {
                            ...entry,
                            subjectCodeDisplay: subjectObj?.subjectCode || entry.subjectFormField || 'N/A',
                            subjectNameDisplay: subjectObj?.subjectName || 'N/A',
                            facultyName: fullName,
                            timeStartFormatted: entry.timeStart ? dayjs(entry.timeStart, 'HH:mm').format('hh:mm A') : 'N/A',
                            timeEndFormatted: entry.timeEnd ? dayjs(entry.timeEnd, 'HH:mm').format('hh:mm A') : 'N/A',
                        };
                    })
                };
                setPreviewData(formattedValues);
                setIsPreviewModalVisible(true);
            })
            .catch(info => {
                toast.error(`Please correct required fields before preview.`);
            });
    };

    const handleSubmitFromPreview = async () => {
        const valuesFromForm = form.getFieldsValue(true);
        if (isViewMode) return;
        setIsSubmitting(true);
        setFormError(null);

        const payload = {
            academicYear: valuesFromForm.academicYear,
            academicTerm: valuesFromForm.academicTerm,
            gradeLevel: valuesFromForm.gradeLevel,
            strand: valuesFromForm.strand || null,
            section: valuesFromForm.section,
            status: valuesFromForm.status,
            scheduleEntries: valuesFromForm.scheduleEntries?.map(entry => {
                const subjectObj = subjectsList.find(s => s.subjectCode === entry.subjectFormField);
                return {
                    subjectRef: subjectObj?._id || null,
                    facultyRef: entry.facultyRef || null,
                    room: entry.room?.trim() || null,
                    days: entry.days || [],
                    timeStart: entry.timeStart || null,
                    timeEnd: entry.timeEnd || null,
                };
            }).filter(Boolean) || []
        };

        if (payload.scheduleEntries.length === 0) {
            toast.error("At least one valid subject entry is required.");
            setIsSubmitting(false); return;
        }

        try {
            const method = isEditMode ? 'PATCH' : 'POST';
            const url = isEditMode ? `${API_URL}/api/admin/class-schedules/${scheduleId}` : `${API_URL}/api/admin/class-schedules`;
            const response = await fetch(url, {
                method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
            });
            const responseData = await response.json();
            if (!response.ok) throw new Error(responseData.message || `API Error`);
            
            toast.success(responseData.message || `Schedule ${isEditMode ? 'updated' : 'created'} successfully!`);
            setIsPreviewModalVisible(false);
            navigate('/user-admin/schedule');
        } catch (error) {
            setFormError(error.message);
            toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (initialDataLoading && !isInitialSetupComplete) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="Loading Schedule Details..." />
            </div>
        );
    }

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <div className="arrows" onClick={() => navigate('/user-admin/schedule')} role="button" tabIndex={0}>
                            <MdOutlineKeyboardArrowLeft />
                        </div>
                        <p className="heading">{pageTitle}</p>
                    </div>

                    {formError && !isPreviewModalVisible && (
                        <Alert message={formError} type="error" showIcon closable onClose={() => setFormError(null)} style={{ marginBottom: 16 }} />
                    )}

                    <Form form={form} layout="vertical" onFinish={handlePreview} initialValues={{ status: 'Pending', scheduleEntries: [] }}>
                        <div className="container-columns">
                            <div className="column">
                                <div className="group-title"><IoMdInformationCircle className="section-icon" /><p className="section-title">ACADEMIC DETAILS</p></div>
                                <Form.Item label="Academic Year" name="academicYear" rules={[{ required: true }]}>
                                    <Select placeholder="Select year" loading={loadingAcademicYears} disabled={fieldsDisabled || isCreateMode || isEditMode}>
                                        {academicYears.map(y => (<Option key={y._id || y.Year} value={y.Year}>{y.Year}</Option>))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Academic Term" name="academicTerm" rules={[{ required: true }]}>
                                    <Select placeholder="Select term" loading={loadingAcademicTerms} disabled={fieldsDisabled || isCreateMode || isEditMode}>
                                        {academicTerms.map(t => (<Option key={t._id || t.term} value={t.term}>{t.term}</Option>))}
                                    </Select>
                                </Form.Item>
                            </div>
                            <div className="column">
                                <div className="group-title"><IoMdInformationCircle className="section-icon" /><p className="section-title">CLASS IDENTIFICATION</p></div>
                                <Form.Item label="Grade Level" name="gradeLevel" rules={[{ required: true }]}>
                                    <Select placeholder="Select grade level" loading={loadingGradeLevels} disabled={fieldsDisabled}>
                                        {gradeLevels.map(gl => (<Option key={gl._id || gl.level} value={gl.level}>{gl.level}</Option>))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Strand" name="strand">
                                    <Select placeholder="Select strand (if applicable)" loading={loadingStrands} disabled={fieldsDisabled} allowClear>
                                        {strands.map(s => (<Option key={s._id || s.strand} value={s.strand}>{s.strand}</Option>))}
                                    </Select>
                                </Form.Item>
                            </div>
                            <div className="column">
                                <div className="group-title"><IoMdInformationCircle className="section-icon" /><p className="section-title">SECTION & STATUS</p></div>
                                <Form.Item label="Section" name="section" rules={[{ required: true }]}>
                                    <Select placeholder="Select section" loading={loadingSections} disabled={fieldsDisabled || !selectedGradeLevel}>
                                        {sections.map(sec => (<Option key={sec._id || sec.sectionName} value={sec.sectionName}>{sec.sectionName}</Option>))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Schedule Status" name="status" rules={[{ required: true }]}>
                                    <Select placeholder="Select status" disabled={fieldsDisabled}>
                                        <Option value="Pending">Pending</Option>
                                        <Option value="Active">Active</Option>
                                        <Option value="Inactive">Inactive</Option>
                                    </Select>
                                </Form.Item>
                            </div>
                        </div>

                        <Title level={4} style={{ marginTop: 10, marginBottom: 16, borderTop: '1px solid #f0f0f0', paddingTop: 20 }}>Interactive Schedule Builder</Title>
                        
                        <div className="schedule-builder-layout">
                            <div className="schedule-visualizer-container">
                                <ScheduleVisualizer
                                    scheduleEntries={watchedScheduleEntries}
                                    facultyList={allFacultyAccounts}
                                    subjectsList={subjectsList}
                                    onBlockClick={handleEditEntry}
                                    isViewMode={isViewMode}
                                />
                            </div>
                            <div className="subject-selector-container">
                               <SubjectSelector
                                    subjects={subjectsList}
                                    scheduledEntries={watchedScheduleEntries || []}
                                    onSelectSubject={handleAddEntry}
                                    loading={loadingSubjects}
                                    disabled={isViewMode || !selectedGradeLevel || !selectedAcademicTerm}
                                />
                            </div>
                        </div>

                        <Form.Item name="scheduleEntries" hidden>
                            <Input />
                        </Form.Item>

                        {!isViewMode && (
                            <Form.Item style={{ marginTop: 30, textAlign: 'right' }}>
                                <Button onClick={() => navigate('/user-admin/schedule')} style={{marginRight: 8}}>Cancel</Button>
                                <Button type="primary" htmlType="submit" loading={isSubmitting || initialDataLoading}>
                                    {isEditMode ? 'Preview & Update Schedule' : 'Preview & Save Schedule'}
                                </Button>
                            </Form.Item>
                        )}
                         {isViewMode && (
                            <Form.Item style={{ marginTop: 30, textAlign: 'right' }}>
                                <Button onClick={() => navigate('/user-admin/schedule')}>Back to List</Button>
                            </Form.Item>
                        )}
                    </Form>
                </main>
            </div>
            
            {isEntryModalVisible && (
                <ScheduleEntryModal
                    visible={isEntryModalVisible}
                    onClose={() => { setIsEntryModalVisible(false); setEditingEntry(null); }}
                    onSave={handleSaveEntry}
                    onRemove={handleRemoveEntry}
                    allFacultyAccounts={allFacultyAccounts}
                    allFacultyProfiles={allFacultyProfiles}
                    availableRooms={availableRooms}
                    loadingRooms={loadingRooms}
                    initialData={editingEntry}
                    subjectsList={subjectsList}
                    gradeLevel={selectedGradeLevel}
                    currentScheduleEntries={watchedScheduleEntries || []}
                    currentScheduleId={scheduleId}
                    academicYear={selectedAcademicYear}
                    academicTerm={selectedAcademicTerm}
                />
            )}

            {previewData && (
                <Modal
                    title={<Title level={4} style={{ margin: 0 }}>Class Schedule Preview</Title>}
                    open={isPreviewModalVisible}
                    onOk={handleSubmitFromPreview}
                    onCancel={() => setIsPreviewModalVisible(false)}
                    width={Math.min(800, window.innerWidth - 40)}
                    okText={isEditMode ? "Confirm Update" : "Confirm Save"}
                    cancelText="Edit Further"
                    confirmLoading={isSubmitting}
                >
                    <Descriptions bordered column={1} size="small" style={{ marginBottom: 20 }}>
                        <Descriptions.Item label="Academic Year">{previewData.academicYear}</Descriptions.Item>
                        <Descriptions.Item label="Term">{previewData.academicTerm}</Descriptions.Item>
                        <Descriptions.Item label="Grade Level">{previewData.gradeLevel}</Descriptions.Item>
                        <Descriptions.Item label="Strand">{previewData.strand || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Section">{previewData.section}</Descriptions.Item>
                        <Descriptions.Item label="Status"><Tag>{previewData.status}</Tag></Descriptions.Item>
                    </Descriptions>

                    <Title level={5}>Entries Preview:</Title>
                    {previewData.scheduleEntries?.length > 0 ? (
                        <List
                            dataSource={previewData.scheduleEntries}
                            renderItem={(item, index) => (
                                <List.Item>
                                    <Text strong>{index + 1}. {item.subjectCodeDisplay} - {item.subjectNameDisplay}</Text>
                                    <div style={{ paddingLeft: 18, fontSize: '0.9em' }}>
                                        <Text>Faculty: {item.facultyName}</Text><br/>
                                        <Text>Room: {item.room}</Text><br/>
                                        <Text>Days: {item.days?.join(', ')}</Text><br/>
                                        <Text>Time: {item.timeStartFormatted} - {item.timeEndFormatted}</Text>
                                    </div>
                                </List.Item>
                            )}
                        />
                    ) : <Empty description="No valid entries to preview." />}
                </Modal>
            )}

            <Footer />
        </div>
    );
};

export default ScheduleForm;